// Лиды: сколько пришло и чем кончилось. Источник правды — сеть (синхронизация каждые 10 минут);
// цифры считает платформа. Одна полоса фильтров: период, статус, оффер, источник, поиск и сортировка —
// итоги над таблицей считаются с теми же фильтрами (кроме статуса, чтобы видеть разбивку).
import { Button, Chip, dateShort, EmptyState, pct, Skeleton, usd, when } from '@autoaff/ui';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { ArrowUpDown, PlugZap, Search, SearchX, ShieldCheck, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import {
  FRAUD_LEVEL,
  fraudLevelOf,
  type LeadFilter,
  type LeadSort,
  type LeadSource,
  type LeadsResponse,
  leadStatus,
  money,
  useIntegrations,
} from '../business';
import {
  FilterBar,
  type FilterOption,
  FilterSelect,
  PeriodPicker,
  periodKey,
  periodPhrase,
  ResetFilters,
  usePeriod,
  useRemembered,
} from '../filters';
import { PageHead } from './common';
import { LeadCard } from './LeadCard';

const STATUSES: Array<{ value: LeadFilter; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'in_process', label: 'В обработке' },
  { value: 'approved', label: 'Апрув' },
  { value: 'rejected', label: 'Отказ' },
  { value: 'trash', label: 'Мусор' },
  { value: 'risky', label: 'Подозрительные' },
];

const SOURCES: Array<{ value: LeadSource | 'all'; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'platform', label: 'Наши лендинги' },
  { value: 'legacy', label: 'Panda-Manda' },
  { value: 'network', label: 'Кабинет buyo' },
];

const SORTS: Array<FilterOption<LeadSort>> = [
  { value: 'new', label: 'Сначала новые' },
  { value: 'old', label: 'Сначала старые' },
  { value: 'payout', label: 'По выплате' },
];
const SORT_KEYS = SORTS.map((s) => s.value);

/** Поиск уходит в api, когда перестали печатать. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function LeadsPage() {
  const { lead, show } = useSearch({ from: '/leads' });
  const [period, setPeriod] = usePeriod('leads', '30d');
  const [status, setStatusState] = useState<LeadFilter>(show === 'risky' ? 'risky' : 'all');
  const [offer, setOffer] = useState('all');
  const [source, setSource] = useState<LeadSource | 'all'>('all');
  const [text, setText] = useState('');
  const [sort, setSort] = useRemembered<LeadSort>('leads:sort', 'new', SORT_KEYS);
  const q = useDebounced(text.trim(), 300);
  const integ = useIntegrations();
  const navigate = useNavigate({ from: '/leads' });
  const router = useRouter();

  // Ссылка «?show=risky» (из алерта или сводки) — сразу подозрительные
  useEffect(() => {
    if (show === 'risky') setStatusState('risky');
  }, [show]);
  const setStatus = (s: LeadFilter) => {
    setStatusState(s);
    // Выбрали другой статус — ссылка больше не про подозрительных, иначе обновление вернёт их
    if (show && s !== 'risky')
      void navigate({ search: (p) => ({ ...p, show: undefined }), replace: true, resetScroll: false });
  };

  // Открыли карточку из списка — закрываем шагом «назад», чтобы история не копила открытия;
  // пришли по прямой ссылке — просто убираем параметр
  const openedHere = useRef(false);
  const closeCard = useCallback(() => {
    if (openedHere.current) {
      openedHere.current = false;
      router.history.back();
    } else void navigate({ search: (p) => ({ ...p, lead: undefined }), replace: true, resetScroll: false });
  }, [navigate, router]);

  const params = new URLSearchParams({ period: periodKey(period), status, sort });
  if (offer !== 'all') params.set('offer', offer);
  if (source !== 'all') params.set('source', source);
  if (q) params.set('q', q);
  const query = params.toString();
  const lq = useInfiniteQuery({
    queryKey: ['leads', query],
    queryFn: ({ pageParam }) =>
      api<LeadsResponse>(`/api/leads?${query}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? last.nextBefore,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const first = lq.data?.pages[0];
  const c = first?.counts;
  const items = lq.data?.pages.flatMap((p) => p.items) ?? [];
  // % апрува от решённых считает api (одна формула платформы); старый api — так же здесь
  const decided = c ? c.approved + c.rejected + c.trash : 0;
  const approveRate = c?.approveRate ?? (c && decided > 0 ? (c.approved / decided) * 100 : null);
  const buyo = integ.data?.buyo;
  const phrase = periodPhrase(period);
  const filtered = status !== 'all' || offer !== 'all' || source !== 'all' || q !== '';
  const reset = () => {
    setStatus('all');
    setOffer('all');
    setSource('all');
    setText('');
    setSort('new');
  };

  const statusOpts = useMemo(
    () =>
      STATUSES.filter((s) => s.value !== 'risky' || (c?.risky ?? 0) > 0 || status === 'risky').map((s) => ({
        ...s,
        ...(c ? { count: s.value === 'all' ? c.all : c[s.value] } : {}),
      })),
    [c, status],
  );
  const offerOpts = useMemo(() => {
    const list = first?.offers ?? [];
    const opts: Array<FilterOption<string>> = [
      { value: 'all', label: 'Все', count: list.reduce((a, o) => a + o.n, 0) },
      ...list.map((o) => ({ value: o.key, label: o.name ?? 'Без оффера', count: o.n })),
    ];
    // Выбранный оффер остаётся в списке, даже если за новый период у него нет лидов
    if (offer !== 'all' && !list.some((o) => o.key === offer))
      opts.push({ value: offer, label: offer === '-' ? 'Без оффера' : offer, count: 0 });
    return opts;
  }, [first, offer]);
  const sourceOpts = useMemo(() => {
    const n = first?.sources;
    return SOURCES.map((s) => ({
      ...s,
      ...(n ? { count: s.value === 'all' ? n.platform + n.legacy + n.network : n[s.value] } : {}),
    }));
  }, [first]);

  return (
    <>
      <PageHead
        title="Лиды"
        sub={
          buyo?.sync?.lastAt
            ? `Статусы из buyo обновлены ${when(buyo.sync.lastAt)}`
            : 'Статусы приходят из buyo каждые 10 минут'
        }
      />
      <div className="pc">
        {buyo && !buyo.connected ? (
          <div className="card">
            <EmptyState
              icon={<PlugZap />}
              title="Подключите buyo"
              action={
                <Link to="/settings" hash="buyo" className="btn btn-primary">
                  Открыть интеграции
                </Link>
              }
            >
              Лиды и их статусы платформа берёт из кабинета buyo. Вставьте ключ API в настройках — первые лиды
              появятся через пару минут.
            </EmptyState>
          </div>
        ) : (
          <>
            <FilterBar
              active={
                [periodKey(period) !== '30d', status !== 'all', offer !== 'all', source !== 'all'].filter(
                  Boolean,
                ).length
              }
              search={
                <label className="search-field filter-end">
                  <Search aria-hidden />
                  <input
                    type="search"
                    placeholder="Имя или номер"
                    aria-label="Найти лид по имени или номеру"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                </label>
              }
              end={
                <FilterSelect
                  label="Сортировка"
                  value={sort}
                  onChange={setSort}
                  options={SORTS}
                  allValue="new"
                  icon={<ArrowUpDown aria-hidden />}
                  width={220}
                  showValue
                />
              }
            >
              <PeriodPicker value={period} onChange={setPeriod} />
              <FilterSelect
                label="Статус"
                value={status}
                onChange={setStatus}
                options={statusOpts}
                allValue="all"
              />
              <FilterSelect
                label="Оффер"
                value={offer}
                onChange={setOffer}
                options={offerOpts}
                allValue="all"
              />
              <FilterSelect
                label="Источник"
                value={source}
                onChange={setSource}
                options={sourceOpts}
                allValue="all"
              />
              <ResetFilters shown={filtered || sort !== 'new'} onClick={reset} />
            </FilterBar>

            <section className="kpis" aria-label={`Итоги ${phrase}`}>
              {!c ? (
                [0, 1, 2, 3].map((i) => (
                  <div key={i} className="kpi">
                    <Skeleton w="50%" />
                    <Skeleton w="70%" h={26} />
                  </div>
                ))
              ) : (
                <>
                  <div className="kpi">
                    <span>Лидов {phrase}</span>
                    <b>{c.all}</b>
                  </div>
                  <div className="kpi">
                    <span>В обработке</span>
                    <b>{c.in_process}</b>
                    <small>
                      {c.expApproves !== undefined && c.in_process > 0
                        ? `≈ ${c.expApproves.toFixed(1)} станут апрувом`
                        : 'могут стать апрувом'}
                    </small>
                  </div>
                  <div className="kpi">
                    <span>Апрувов</span>
                    <b>{c.approved}</b>
                    <small>
                      {approveRate !== null ? `${pct(approveRate)} от решённых` : 'пока без решений'}
                    </small>
                  </div>
                  <div className="kpi">
                    <span>Выплаты {phrase}</span>
                    <b>{usd(c.revenue)}</b>
                    <small>за апрувы</small>
                  </div>
                </>
              )}
            </section>

            <div
              className={clsx('card leads-card', lq.isPlaceholderData && 'is-stale')}
              aria-busy={lq.isFetching && !lq.isFetchingNextPage}
            >
              {lq.isPending && (
                <div className="flex flex-col gap-3" style={{ padding: 16 }}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} h={20} />
                  ))}
                </div>
              )}
              {lq.isError && (
                <EmptyState
                  icon={<Users />}
                  title="Не удалось загрузить лиды"
                  action={<Button onClick={() => lq.refetch()}>Повторить</Button>}
                >
                  {(lq.error as Error).message}
                </EmptyState>
              )}
              {lq.isSuccess &&
                items.length === 0 &&
                (status === 'risky' && !(offer !== 'all' || source !== 'all' || q) ? (
                  <EmptyState icon={<ShieldCheck />} title="Подозрительных лидов нет">
                    За период нет лидов с высоким риском и задержанных антифродом.
                  </EmptyState>
                ) : filtered ? (
                  <EmptyState
                    icon={<SearchX />}
                    title="Под эти фильтры лидов нет"
                    action={<Button onClick={reset}>Сбросить фильтры</Button>}
                  >
                    {q
                      ? `По запросу «${q}» ${phrase} ничего не нашлось.`
                      : `Попробуйте другой статус или период.`}
                  </EmptyState>
                ) : (
                  <EmptyState icon={<Users />} title="Лидов за период нет">
                    {buyo?.sync?.lastAt
                      ? 'Как только в кабинете buyo появятся лиды, они будут здесь.'
                      : 'Первая синхронизация с buyo ещё идёт — обычно это пара минут.'}
                  </EmptyState>
                ))}
              {items.length > 0 && (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Когда</th>
                      <th>Клиент</th>
                      <th>Статус</th>
                      <th>Оффер</th>
                      <th className="num">Выплата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((l) => {
                      const st = leadStatus(l.status);
                      return (
                        <tr key={l.id} className="row-open">
                          <td data-m="hide" className="muted" style={{ whiteSpace: 'nowrap' }}>
                            {when(l.createdAt)}
                          </td>
                          <td>
                            <Link
                              to="/leads"
                              search={(s) => ({ ...s, lead: l.id })}
                              resetScroll={false}
                              className="row-link"
                              onClick={() => {
                                openedHere.current = true;
                              }}
                            >
                              {l.name ?? 'Без имени'}
                            </Link>
                            <span className="sub mono">{l.phone ?? '—'}</span>
                          </td>
                          <td data-m="end">
                            <Chip tone={st.tone}>{st.label}</Chip>
                            {l.held ? (
                              <span className="sub risk-note" data-tone="warning">
                                задержан антифродом
                              </span>
                            ) : (
                              l.risk !== null &&
                              fraudLevelOf(l.risk) === 'high' && (
                                <span className="sub risk-note" data-tone="danger">
                                  {FRAUD_LEVEL.high.label} · {l.risk}
                                </span>
                              )
                            )}
                            {l.rejectReason && <span className="sub">{l.rejectReason}</span>}
                          </td>
                          <td className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
                            {l.offer ?? '—'}
                            <span className="sub only-m">{when(l.createdAt)}</span>
                          </td>
                          <td className="num" data-m="end">
                            {l.payout !== null && l.status === 'approved'
                              ? money(l.payout, l.payoutCurrency)
                              : '—'}
                            {l.payoutStatus === 'hold' && l.holdEndAt && (
                              <span className="sub">холд до {dateShort(l.holdEndAt)}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {lq.hasNextPage && (
                <div className="leads-more">
                  <Button block onClick={() => lq.fetchNextPage()} disabled={lq.isFetchingNextPage}>
                    {lq.isFetchingNextPage ? 'Загружаю…' : 'Показать ещё'}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <LeadCard id={lead} onClose={closeCard} />
    </>
  );
}
